import type { TCPOffsetOption } from '@sb/motion-planning';
import type {
  EquipmentStateItem,
  RoutineContext,
  StepFailure,
} from '@sb/routine-runner';
import { FailureKind } from '@sb/routine-runner';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';

import type { MillerWeldParameters } from '../../MillerWeldMachine/types/MillerWeldMachineCommands';
import { GAS_FLOW_DISABLED } from '../../MillerWeldMachine/types/MillerWeldMachineCommands';

/**
 * Arc control and torch-frame pinning, shared by the weld steps.
 *
 * Both `WeldStep` and `RotaryWeldStep` drive the same power source the same
 * way — set parameters, strike, extinguish — and differ only in what moves
 * while the arc is live. This holds the part they share, so a fix to the
 * Miller command shape or the unsupported-machine reporting lands in one
 * place rather than in whichever step the author happened to be reading.
 *
 * Free functions taking an explicit context, rather than a base class: the
 * steps already extend `Step`, and their `Variables` differ (only `WeldStep`
 * carries `weldOnResume`), so the variable writes stay with the caller.
 */

/**
 * What every command below needs: who to talk to, and where to report.
 *
 * `fail` is awaited, so a caller that returns early on a refused command
 * does so after the routine has taken the failure.
 */
type WeldMachineTarget = {
  routineContext: RoutineContext;
  selectedMachineID: string | undefined;
  fail: (failure: StepFailure) => Promise<void> | void;
};

/**
 * The step's selected machine and torch, as the equipment layer currently
 * sees them. Re-read per call rather than cached: equipment state is
 * republished as the cell changes, and a stale item would be written through
 * by `setVisualizerWeldArcFlashEffect`.
 */
export function getActiveWeldEquipment({
  routineContext,
  selectedMachineID,
  selectedTorchID,
}: {
  routineContext: RoutineContext;
  selectedMachineID: string | undefined;
  selectedTorchID: string | undefined;
}): {
  weldMachine: EquipmentStateItem | undefined;
  torch: EquipmentStateItem | undefined;
} {
  const allEquipment = routineContext.equipment.getEquipmentState();

  const weldMachine = allEquipment.find(
    (equipment) => equipment.id === selectedMachineID,
  );

  const torch = allEquipment.find(
    (equipment) => equipment.id === selectedTorchID,
  );

  return { weldMachine, torch };
}

/**
 * The TCP frame the selected torch resolves to.
 *
 * @throws when no torch is selected or the selection is not loaded — a weld
 * cannot compute targets without knowing where its torch is.
 */
export function getWeldTorchTCPOffsetOption({
  torch,
  selectedTorchID,
}: {
  torch: EquipmentStateItem | undefined;
  selectedTorchID: string | undefined;
}): TCPOffsetOption {
  if (torch == null) {
    throw new Error('Torch not found');
  }

  if (selectedTorchID == null) {
    throw new Error('Selected torch ID is null');
  }

  // TCP Offset option a bit of a hack. May need to revisit the indexing once we add dedicated torches, not just custom grippers.
  // See https://github.com/standardbots/sb/blob/540d718e5d2bbe3226ea9b4128b9f49392bfc6ef/libs/integrations/implementations/CustomGripper/frontend.tsx#L30
  return `ee-${torch.state.kind}-0`;
}

export function setVisualizerWeldArcFlashEffect({
  routineContext,
  selectedMachineID,
  isWelding,
}: {
  routineContext: RoutineContext;
  selectedMachineID: string | undefined;
  isWelding: boolean;
}): void {
  // Get current weld machine state
  const { weldMachine } = getActiveWeldEquipment({
    routineContext,
    selectedMachineID,
    selectedTorchID: undefined,
  });

  // TODO: fix the return type of activeEquipment.weldMachine to
  // specify that these are weld machine state items (then we don't need to check for 'isWelding' in the state)
  if (weldMachine && 'isWelding' in weldMachine.state) {
    weldMachine.state.isWelding = isWelding;
  }
}

export async function stopMillerWeldMachine({
  routineContext,
  selectedMachineID,
  fail,
}: WeldMachineTarget): Promise<void> {
  try {
    await routineContext.equipment.executeCommand({
      kind: 'MillerWeldMachineCommand',
      deviceID: selectedMachineID,
      subCommand: {
        kind: 'stopArc',
      },
    });
  } catch (error) {
    await fail({
      failure: { kind: FailureKind.WeldFailure },
      failureReason: `Failed to stop weld machine: ${error.message}`,
      error,
    });
  }
}

/**
 * @returns whether the arc was struck. `false` has already been reported.
 */
export async function startMillerWeldMachine({
  routineContext,
  selectedMachineID,
  fail,
}: WeldMachineTarget): Promise<boolean> {
  try {
    await routineContext.equipment.executeCommand({
      kind: 'MillerWeldMachineCommand',
      deviceID: selectedMachineID,
      subCommand: {
        kind: 'startArc',
      },
    });

    return true;
  } catch (error) {
    await fail({
      failure: { kind: FailureKind.WeldFailure },
      failureReason: `Failed to start weld machine: ${error.message}`,
      error,
    });

    return false;
  }
}

/**
 * @returns whether the machine accepted the parameters. `false` has already
 * been reported.
 */
export async function setMillerWeldParameters({
  routineContext,
  selectedMachineID,
  millerWeldParameters,
  fail,
}: WeldMachineTarget & {
  millerWeldParameters: MillerWeldParameters;
}): Promise<boolean> {
  // Gas timing rides along only when the flag is on, so a flag-off robot sends
  // the same registers it always has.
  //
  // These are read with a fallback rather than relying on the schema default:
  // step arguments arrive here unparsed, so a routine saved before pre/postflow
  // existed has neither key.
  const { preflow, postflow, ...weldParameters } = millerWeldParameters;

  const gasFlowEnabled = getFeatureFlagSync('millerWeldIntegrationV2', true);

  try {
    await routineContext.equipment.executeCommand({
      kind: 'MillerWeldMachineCommand',
      deviceID: selectedMachineID,
      subCommand: {
        kind: 'setWeldParameters',
        weldTravelSpeed: 0, // This is disregarded by machine. Just set it like this for now.
        ...weldParameters,
        ...(gasFlowEnabled
          ? {
              preflow: preflow ?? GAS_FLOW_DISABLED,
              postflow: postflow ?? GAS_FLOW_DISABLED,
            }
          : {}),
      },
    });

    return true;
  } catch (error) {
    await fail({
      failure: { kind: FailureKind.WeldFailure },
      failureReason: `Failed to set weld machine parameters: ${error.message}`,
      error,
    });

    return false;
  }
}

/**
 * Reports a machine the weld steps cannot drive. The three commands below
 * answer identically for every kind but Miller, so they share this arm.
 */
async function failUnsupportedWeldMachine({
  weldMachine,
  fail,
}: {
  weldMachine: EquipmentStateItem;
  fail: WeldMachineTarget['fail'];
}): Promise<void> {
  let failureReason: string;

  switch (weldMachine.kind) {
    case 'EsabWeldMachine':
      failureReason = 'ESAB weld machine not supported';

      break;
    case 'WeldMachine':
      failureReason = 'Generic weld machine not supported';

      break;
    default:
      failureReason = `Unknown weld machine type: ${weldMachine.kind}`;

      break;
  }

  await fail({
    failure: {
      kind: FailureKind.WeldFailure,
    },
    failureReason,
  });
}

/**
 * Stop the current weld machine.
 *
 * Weld stop commands may be sent even if welding hasn't started yet.
 */
export async function stopWeldMachine({
  routineContext,
  selectedMachineID,
  weldMachine,
  fail,
}: WeldMachineTarget & { weldMachine: EquipmentStateItem }): Promise<void> {
  if (weldMachine.kind === 'MillerWeldMachine') {
    await stopMillerWeldMachine({ routineContext, selectedMachineID, fail });

    setVisualizerWeldArcFlashEffect({
      routineContext,
      selectedMachineID,
      isWelding: false,
    });

    return;
  }

  await failUnsupportedWeldMachine({ weldMachine, fail });
}

/**
 * @returns whether the arc was struck. On `false` the failure has already
 * been reported, and the caller must not go on to anything that assumes a
 * live arc — `fail` does not throw back.
 */
export async function startWeldMachine({
  routineContext,
  selectedMachineID,
  weldMachine,
  fail,
}: WeldMachineTarget & { weldMachine: EquipmentStateItem }): Promise<boolean> {
  if (weldMachine.kind === 'MillerWeldMachine') {
    const didStart = await startMillerWeldMachine({
      routineContext,
      selectedMachineID,
      fail,
    });

    if (didStart) {
      setVisualizerWeldArcFlashEffect({
        routineContext,
        selectedMachineID,
        isWelding: true,
      });
    }

    return didStart;
  }

  await failUnsupportedWeldMachine({ weldMachine, fail });

  return false;
}

/**
 * @returns whether the machine accepted the parameters. On `false` the
 * failure has already been reported.
 */
export async function setWeldMachineParameters({
  routineContext,
  selectedMachineID,
  weldMachine,
  millerWeldParameters,
  fail,
}: WeldMachineTarget & {
  weldMachine: EquipmentStateItem;
  millerWeldParameters: MillerWeldParameters;
}): Promise<boolean> {
  if (weldMachine.kind === 'MillerWeldMachine') {
    return setMillerWeldParameters({
      routineContext,
      selectedMachineID,
      millerWeldParameters,
      fail,
    });
  }

  await failUnsupportedWeldMachine({ weldMachine, fail });

  return false;
}
